
import React, { useState } from 'react';
import { EnhancedCrawlResult } from '@/types/crawler';
import { DashboardWidgetRefs, RetryHandlers } from '@/components/dashboard/types';
import DashboardWidgets from '@/components/dashboard/DashboardWidgets';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, Grid, List } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DashboardWidgetContainerProps {
  crawlResult: EnhancedCrawlResult;
  selectedPageUrl: string | null;
  widgetRefs: DashboardWidgetRefs;
  onSelectPage: (url: string) => void;
  retryHandlers: RetryHandlers;
}

const DashboardWidgetContainer: React.FC<DashboardWidgetContainerProps> = ({
  crawlResult,
  selectedPageUrl,
  widgetRefs,
  onSelectPage,
  retryHandlers
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  
  return (
    <div className="space-y-6" data-testid="dashboard-widgets-container">
      {/* Widget Controls */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div className="space-y-2">
          <h2 className="text-2xl font-bold text-foreground">
            <span className="header-gradient fallback-gradient">AI Analysis Tools</span>
          </h2>
          <p className="text-muted-foreground text-sm">
            Comprehensive insights powered by advanced AI analysis
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <div className="flex items-center rounded-lg border border-border/50 p-1">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className="h-8 px-3"
            >
              <Grid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="h-8 px-3"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Expand/Collapse Toggle */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-2"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="h-4 w-4" />
                Collapse All
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4" />
                Expand All
              </>
            )}
          </Button>
        </div>
      </div>
      
      {/* Widgets Container */}
      <div 
        className={cn(
          "transition-all duration-500 ease-out overflow-hidden",
          isExpanded ? "opacity-100 max-h-none" : "opacity-70 max-h-96",
          viewMode === 'grid' ? "space-y-10" : "space-y-6"
        )}
      >
        <DashboardWidgets
          crawlResult={crawlResult}
          selectedPageUrl={selectedPageUrl}
          widgetRefs={widgetRefs}
          onSelectPage={onSelectPage}
          retryHandlers={retryHandlers}
        />
      </div>
      
      {!isExpanded && (
        <div className="text-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(true)}
            className="text-muted-foreground hover:text-foreground"
          >
            <ChevronDown className="h-4 w-4 mr-2" />
            Show all analysis tools
          </Button>
        </div>
      )}
    </div>
  );
};

export default DashboardWidgetContainer;
